import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UNAUTHORIZED } from 'http-status-codes';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AuthService } from 'wflow-main-app/src/app/auth/auth.service';
import {
  UserCustomizationAPIService,
  UserIdentityAPIService,
  UserOrgAccountAPIService,
} from 'wflow-main-app/src/app/core/api';
import { APP_ROUTES } from 'wflow-main-app/src/app/routing.const';
import { IdleStrategy } from '../../auth/idle.strategy';
import { BackendError } from '../../core/services/error-handler/basic-error-handler.service';
import { DEFAULT_ORG_START_PAGE_ROUTE } from '../../pages/org/consts/start-page.const';
import {
  LoadGlobalProfileSuccess,
  LoadSuccessOrgUserData,
  SetFailure,
  SetOrgUserDataFailure,
  UserActionTypes,
} from './user.actions';
import { UserContextProfile } from './user.model';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userAccountService = inject(UserOrgAccountAPIService);
  private readonly userIdentityService = inject(UserIdentityAPIService);
  private readonly userCustomizationSvc = inject(UserCustomizationAPIService);
  private readonly authSvc = inject(AuthService);
  private readonly idleStrategy = inject(IdleStrategy);

  loadGlobalProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.LOAD_GLOBAL_PROFILE),
      switchMap(() =>
        this.userIdentityService.getAccountInfo().pipe(
          map((account) => {
            /** Start the idle strategy */
            if (account?.minimalInactivityTimeout) {
              this.idleStrategy.startWith(account.minimalInactivityTimeout);
            }

            if (account?.login || this.authSvc.profile == null) {
              return account;
            }

            const profile = this.authSvc.profile;
            return {
              ...account,
              login: profile.email,
              firstName: profile.given_name,
              lastName: profile.family_name,
              fullName: [profile.given_name, profile.family_name]
                .join(' ')
                .trim(),
            };
          }),
          map((account) => LoadGlobalProfileSuccess({ account })),
          catchError((error) => of(SetFailure({ error }))),
        ),
      ),
    ),
  );

  /** User data */
  loadOrgUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.LOAD_ORG_USER_DATA),
      mergeMap((payload: { orgId: string }) =>
        this._getOrgProfile(payload.orgId).pipe(
          map((data) => LoadSuccessOrgUserData(data)),
          catchError((error) =>
            of(SetOrgUserDataFailure({ orgId: payload.orgId, error })),
          ),
        ),
      ),
    ),
  );

  errorListener$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActionTypes.SET_FAILURE),
        filter((v) => !!v),
        tap((payload: { error: BackendError }) => {
          if (payload?.error?.status === UNAUTHORIZED) {
            this.authSvc.renewToken().pipe(take(1)).subscribe();
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  private _getOrgProfile(orgId: string) {
    return combineLatest([
      this.userAccountService.getAccount(orgId),
      this.userCustomizationSvc.getStartPage(orgId),
    ]).pipe(
      first(),
      map(
        ([account, startUrl]) =>
          ({
            ...account.identity,
            rights: account.rights,
            startRoute: this._getRoute(orgId, startUrl),
            roles: account.roles,
            teams: account.teams,
            hasFullAccess: account.hasFullAccess,
          }) as UserContextProfile,
      ),
      map((profile) => ({
        profile,
        orgId,
      })),
    );
  }

  private _getRoute(orgId: string, startPage: string) {
    if (orgId == null) return startPage;

    const path = [`/${APP_ROUTES.ORGANIZATION}`, orgId];

    const startPageRoute = !!startPage
      ? startPage.split('/')
      : DEFAULT_ORG_START_PAGE_ROUTE;

    path.push(...startPageRoute);

    return path;
  }
}
